import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Briefcase, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { PublicLayout } from "@/components/layout/PublicLayout";

export default function Careers() {
  const [jobs, setJobs] = useState([]);
  const [dept, setDept] = useState("All");

  useEffect(() => {
    api.get("/jobs").then((r) => setJobs(r.data)).catch(() => {});
  }, []);

  const departments = ["All", ...Array.from(new Set(jobs.map((j) => j.department)))];
  const filtered = dept === "All" ? jobs : jobs.filter((j) => j.department === dept);

  return (
    <PublicLayout>
      <section className="relative overflow-hidden border-b border-[var(--aur-border)] bg-[var(--aur-bg-2)] pb-16 pt-36">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <p className="eyebrow fade-up">Careers at Aurelia</p>
          <h1 className="fade-up mt-5 max-w-3xl font-serif text-4xl tracking-tight sm:text-5xl">
            A career composed with the same care as our stays
          </h1>
          <p className="fade-up mt-5 max-w-xl font-sans text-base text-[var(--aur-text-2)]">
            We recruit for character first. Explore openings across our estates — local and international
            candidates are warmly welcomed.
          </p>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-6 py-16 lg:px-10">
        <div className="mb-10 flex flex-wrap gap-3">
          {departments.map((d) => (
            <button
              key={d}
              onClick={() => setDept(d)}
              data-testid={`career-filter-${d.toLowerCase().replace(/\s+/g, "-")}`}
              className={`rounded-full border px-4 py-2 font-sans text-xs uppercase tracking-widest transition-all ${
                dept === d ? "border-gold bg-gold text-[var(--aur-bg)]" : "border-[var(--aur-border)] text-[var(--aur-text-2)] hover:border-gold hover:text-gold"
              }`}
            >
              {d}
            </button>
          ))}
        </div>

        <div className="space-y-4">
          {filtered.map((job) => (
            <Link
              key={job.id}
              to={`/careers/${job.id}`}
              data-testid={`job-card-${job.id}`}
              className="group flex flex-col justify-between gap-4 rounded-lg border border-[var(--aur-border)] bg-[var(--aur-card)] p-6 transition-all hover:border-gold/40 md:flex-row md:items-center"
            >
              <div>
                <div className="mb-2 flex items-center gap-3">
                  <span className="rounded-full bg-gold/10 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-gold">{job.department}</span>
                  <span className="font-mono text-[10px] uppercase tracking-widest text-[var(--aur-text-2)]">{job.level}</span>
                </div>
                <h3 className="font-serif text-2xl tracking-tight">{job.title}</h3>
                <div className="mt-2 flex flex-wrap gap-4 font-sans text-sm text-[var(--aur-text-2)]">
                  <span className="flex items-center gap-1.5"><MapPin size={13} className="text-gold" />{job.location}</span>
                  <span className="flex items-center gap-1.5"><Briefcase size={13} className="text-gold" />{job.employment_type}</span>
                </div>
              </div>
              <span className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-gold">
                View role <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
              </span>
            </Link>
          ))}
          {filtered.length === 0 && (
            <p className="py-16 text-center font-sans text-sm text-[var(--aur-text-2)]">No open positions in this department right now.</p>
          )}
        </div>
      </div>
    </PublicLayout>
  );
}
